import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import html2canvas from 'html2canvas'

import EmailIcon from '../../../images/refer-a-friend/icon_email.svg'
import WhatsappIcon from '../../../images/refer-a-friend/icon_whatsapp.svg'
import TelegramIcon from '../../../images/refer-a-friend/icon_telegram.svg'
import TwitterIcon from '../../../images/refer-a-friend/icon_twitter.svg'
import ShareIcon from '../../../images/refer-a-friend/icon_share.svg'
import { Field } from '../field'
import { Box } from '../../atoms/box'
import { Button } from '../../atoms/button'
import { Flex } from '../../atoms/flex'
import { If } from '../../atoms/if'
import { Image } from '../../atoms/image'
import { Title, Text } from '../../atoms/text'
import { toast } from '../../atoms/toast'
import useDeviceDetect from '../../../hooks/useDeviceDetect'
import { useTranslation } from '../../../context/translationProvider'
import { useReferAFriendApiData } from '../../../hooks/graphqlStaticQuery/useReferAFriendApiData'
import useSlugs from '../../../hooks/useSlugs'
import { QRCode } from '../../atoms/QRCode'
import { getLinkRel } from '../../../utils/getLinkRel'
import { getWebsiteUrl } from '../../../utils/getWebsiteUrl'

const target = '_blank'

export const ReferAFriendAccountMainTab = ({
  toggleTabs,
  referToken,
  remaining,
}) => {
  const { translate } = useTranslation()
  const { isMobile } = useDeviceDetect()
  const [showQRCode, setShowQRCode] = useState(false)
  const QRCodeRef = useRef(null)
  const [referAFriendSlug] = useSlugs(['refer-a-friend'])
  const websiteURL = getWebsiteUrl()
  const referURL = `${websiteURL}${referAFriendSlug}/?referrerCode=${referToken}`

  const message = translate('referAFriend.shareMessage')
  const encodedLink = encodeURIComponent(referURL)
  const encodedMessage = encodeURIComponent(
    message?.replace('{registerLink}', referURL)
  )

  const { content } = useReferAFriendApiData()

  const banner = {
    url: content?.banner_image_mobile?.url,
    width: content?.banner_image_mobile?.width,
  }

  const toggleQRCode = () => {
    setShowQRCode((current) => !current)
    toggleTabs()
  }

  const copy = () => {
    try {
      navigator.clipboard.writeText(referURL)
      toast.success(translate('common.copied'))
    } catch (err) {
      toast.error(translate('common.browserFailure'))
    }
  }

  const handleShare = async () => {
    try {
      const canvas = await html2canvas(
        document.querySelector('#react-qrcode-logo'),
        {
          useCORS: true,
          allowTaint: true,
        }
      )
      const image = canvas?.toDataURL('image/png')
      const img = await fetch(image)
      const imgBlob = await img.blob()

      await navigator.clipboard.write([
        new window.ClipboardItem({
          [imgBlob.type]: imgBlob,
        }),
      ])

      toast.success(translate('common.copied'))
    } catch (error) {
      toast.error(translate('common.browserFailure'))
    }
  }

  const handleNativeShare = async () => {
    if (navigator?.share) {
      try {
        await navigator.share({
          title: translate('navigation.referAFriend'),
          url: referURL,
          text: message?.replace('{registerLink}', ''),
        })
      } catch (err) {
        console.error(err)
      }
    }
  }

  return (
    <Box padding={isMobile ? '0.5em 0 2em 0' : '2em 0'}>
      <Flex>
        <Box width={isMobile ? '100%' : '50%'}>
          <Flex direction="column" gap="1.5em">
            <If
              condition={!isMobile || (isMobile && !showQRCode)}
              render={() => (
                <>
                  <Flex direction="column" gap="0.5em">
                    <Title level={2}>{content?.text}</Title>
                    <Text>{content?.sub_text}</Text>
                  </Flex>
                  <If
                    condition={isMobile}
                    render={() => <Image url={banner?.url} width={'100%'} />}
                  />
                  <Box>
                    <Box marginBottom="5px">
                      <Text noMargin>
                        {translate('referAFriend.yourReferralLink')}
                      </Text>
                    </Box>
                    <Flex gap="0.5em" alignItems="flex-end">
                      <Box width="70%">
                        <Field
                          id="referral-link"
                          disabled
                          forceValue={referURL}
                          noBottomMargin
                        />
                      </Box>
                      <Box width="30%">
                        <Button minWidth="0px" size="small" onClick={copy}>
                          {translate('common.copy')}
                        </Button>
                      </Box>
                    </Flex>
                  </Box>
                </>
              )}
            />

            <If
              condition={isMobile && !showQRCode}
              render={() => (
                <Button bordered dark expand onClick={toggleQRCode}>
                  {translate('referAFriend.generateQRCode')}
                </Button>
              )}
            />

            <If
              condition={isMobile && showQRCode}
              render={() => (
                <Flex justifyContent="center">
                  <QRCode value={referURL} />
                </Flex>
              )}
            />

            <If
              condition={!isMobile || (isMobile && !navigator?.share)}
              render={() => (
                <>
                  <If
                    condition={!isMobile}
                    render={() => (
                      <Text>
                        {`${
                          showQRCode ? translate('common.or') : ''
                        } ${translate('referAFriend.shareLinkOn')}`}
                      </Text>
                    )}
                  />
                  <Flex gap="0.75em" justifyContent={isMobile && 'center'}>
                    <a
                      href={`https://api.whatsapp.com/send?text=${encodedMessage}`}
                      data-action="share/whatsapp/share"
                      target={target}
                      rel={getLinkRel(target)}
                    >
                      <Image url={WhatsappIcon} width={38} />
                    </a>

                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodedMessage}`}
                      target={target}
                      rel={getLinkRel(target)}
                    >
                      <Image url={TwitterIcon} width={38} />
                    </a>
                    <a
                      href={`https://t.me/share/?url=${encodedLink}&text=${encodedMessage}`}
                      target={target}
                      rel={getLinkRel(target)}
                    >
                      <Image url={TelegramIcon} width={38} />
                    </a>
                    <If
                      condition={isMobile}
                      render={() => (
                        <a
                          href={`mailto:?subject=${translate(
                            'referAFriend.email.subject'
                          )}&body=${encodedMessage}`}
                          rel={getLinkRel(target)}
                        >
                          <Image url={EmailIcon} width={38} />
                        </a>
                      )}
                    />
                  </Flex>
                </>
              )}
            />
            <Flex direction="column" gap="0.5em">
              <If
                condition={isMobile && navigator?.share}
                render={() => (
                  <Flex justifyContent="center">
                    <div>
                      <Button
                        size="extraSmall"
                        minWidth="0px"
                        rounded
                        uppercase={false}
                        bold={false}
                        expandMobile={false}
                        black
                        onClick={handleNativeShare}
                      >
                        <Flex alignItems="center" gap="0.25em">
                          <Image url={ShareIcon} width={24} />
                          <Text noMargin style={{ letterSpacing: 0 }}>
                            {translate('common.share')}
                          </Text>
                        </Flex>
                      </Button>
                    </div>
                  </Flex>
                )}
              />
              <Text theme="light" textAlign={isMobile && 'center'}>
                {`${remaining} ${translate('referAFriend.remainingReferrals')}`}
              </Text>
            </Flex>
            <If
              condition={isMobile && showQRCode}
              render={() => (
                <Button expand onClick={toggleQRCode}>
                  {translate('common.done')}
                </Button>
              )}
            />
          </Flex>
        </Box>

        <If
          condition={!isMobile}
          render={() => (
            <Box width={isMobile ? '100%' : '50%'}>
              <Flex
                justifyContent="flex-start"
                alignItems="flex-end"
                height="100%"
              >
                <Flex
                  direction="column"
                  justifyContent="flex-end"
                  alignItems="center"
                  height="100%"
                  gap="1em"
                >
                  <div ref={QRCodeRef}>
                    <QRCode value={referURL} size={200} />
                  </div>
                  <Box>
                    <Button onClick={handleShare} bordered dark>
                      {translate('referAFriend.shareQRCode')}
                    </Button>
                  </Box>
                </Flex>
              </Flex>
            </Box>
          )}
        />
      </Flex>
    </Box>
  )
}

ReferAFriendAccountMainTab.defaultProps = {
  toggleTabs: () => {},
  referToken: '',
  remaining: 0,
}

ReferAFriendAccountMainTab.propTypes = {
  toggleTabs: PropTypes.func,
  referToken: PropTypes.string,
  remaining: PropTypes.number,
}
